import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { Button, Text } from "@radix-ui/themes";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMeasure } from "react-use";
import { twMerge } from "tailwind-merge";
import {
  hitlApi,
  useCreateAutomatedCheckoutMutation,
  useLazyLatestActiveCheckoutQuery,
} from "../../api/hitl-api";
import { useAppDispatch } from "../../store";
import { getCheckoutUrl, getMainUrl } from "../../shared/consts";

const MotionButton = motion(Button);

const variants = {
  initial: () => {
    return { opacity: 0 };
  },
  active: { opacity: 1 },
  exit: () => {
    return { opacity: 0 };
  },
};

const variantsDot = {
  initial: (direction: number) => {
    return { x: `${200 * direction}%` };
  },
  active: { x: "0%" },
  exit: (direction: number) => {
    return { x: `${-200 * direction}%` };
  },
};

const steps = [
  {
    videoSrc: "/instruction-01-cut-600px.webm",
  },
  {
    videoSrc: "/instruction-02-cut-600px.webm",
  },
  {
    videoSrc: "/instruction-03-cut-600px.webm",
  },
  {
    videoSrc: "/instruction-04-cut-600px.webm",
  },
];

export function Guide() {
  const { zoneId = "", storeKey = "" } = useParams();
  const [currentStep, setStep] = useState(0);
  const { t } = useTranslation();
  const [direction, setDirection] = useState<number>(1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [createAutomatedCheckout, createCheckoutResult] = useCreateAutomatedCheckoutMutation();
  const [trigger, latesActiveCheckoutResult] = useLazyLatestActiveCheckoutQuery();

  const startCheckout = async () => {
    const res = await trigger(zoneId).unwrap();

    if (res.result && res.result.id) {
      dispatch(hitlApi.util.upsertQueryData("checkout", res.result.id, res)).then(() => {
        navigate(
          getCheckoutUrl({
            zoneId,
            checkoutKey: res.result.id,
            mode: "sku-in-box",
            storeKey,
          })
        );
      });
    } else {
      createAutomatedCheckout({ zoneId })
        .unwrap()
        .then((res) => {
          navigate(
            getCheckoutUrl({
              zoneId,
              checkoutKey: res.result.key,
              mode: "sku-in-box",
              storeKey,
            })
          );
        });
    }
  };

  return (
    <MotionConfig transition={{ duration: 0.5, type: "spring", bounce: 0 }}>
      <div className="bg-tezzro-bg p-8 h-screen">
        <div className="relative bg-white border rounded-2xl border-card-border w-full h-full flex items-center justify-center">
          <div className="absolute top-4 right-5 z-20">
            <Button
              variant="ghost"
              size="3"
              onClick={() => {
                navigate(
                  getMainUrl({
                    zoneId,
                    storeKey,
                    mode: "sku-in-box",
                  })
                );
              }}
            >
              <ChevronLeftIcon />
              {t("backToMain")}
            </Button>
          </div>
          <div className="flex flex-col w-full h-full p-6 overflow-hidden relative">
            <AnimatePresence mode="popLayout" initial={false} custom={direction}>
              <motion.div
                key={currentStep}
                variants={variants}
                initial="initial"
                animate="active"
                exit="exit"
                custom={direction}
                className="grow"
              >
                <Step videoSrc={steps[currentStep].videoSrc} title={t(`instruction.${currentStep + 1}`)} />
              </motion.div>
            </AnimatePresence>
            <div className="flex gap-2 absolute bottom-[90px] left-1/2 -translate-x-1/2">
              <AnimatePresence mode="popLayout" initial={false} custom={-1 * direction}>
                {steps.map((_, i) => (
                  <div key={i} className="w-2 h-2 bg-black/30 rounded-full">
                    {currentStep === i && (
                      <motion.div
                        variants={variantsDot}
                        initial="initial"
                        animate="active"
                        exit="exit"
                        custom={-1 * direction}
                        className="w-full h-full rounded-full bg-black"
                      ></motion.div>
                    )}
                  </div>
                ))}
              </AnimatePresence>
            </div>
            <div className="flex mt-4">
              <AnimatePresence>
                {currentStep > 0 && (
                  <MotionButton
                    initial={{ y: -12, opacity: 0, filter: "blur(4px)" }}
                    animate={{ y: 0, opacity: 1, filter: "blur(0)" }}
                    exit={{ y: 10, opacity: 0, filter: "blur(4px)" }}
                    size="3"
                    variant="soft"
                    onClick={() => {
                      if (currentStep === 0) {
                        return;
                      }
                      setDirection(-1);
                      setStep((prev) => Math.max(0, prev - 1));
                    }}
                  >
                    {t("prev")}
                  </MotionButton>
                )}
              </AnimatePresence>
              <span className="grow"></span>

              <Button
                size="3"
                loading={createCheckoutResult.isLoading || latesActiveCheckoutResult.isLoading}
                onClick={() => {
                  if (currentStep === steps.length - 1) {
                    startCheckout();
                  } else {
                    setDirection(1);
                    setStep((prev) => prev + 1);
                  }
                }}
              >
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.div
                    initial={{
                      opacity: 0,
                      y: -50,
                      width: currentStep === steps.length - 1 ? 82 : 140,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      width: currentStep === steps.length - 1 ? 140 : 82,
                    }}
                    exit={{
                      opacity: 0,
                      y: 50,
                      width: currentStep === steps.length - 1 ? 82 : 140,
                    }}
                    variants={variantsButton}
                    transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                    key={currentStep === steps.length - 1 ? 1 : 0}
                  >
                    {currentStep === steps.length - 1 ? t("start") : t("next")}
                  </motion.div>
                </AnimatePresence>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MotionConfig>
  );
}

function Step({ videoSrc, title }: { videoSrc: string; title: string }) {
  return (
    <div className={twMerge(["w-full h-full flex flex-col items-center justify-center gap-2"])}>
      <figure
        className={twMerge(["rounded-2xl overflow-hidden max-w-[450px] w-[450px] w-full aspect-square"])}
      >
        <video
          playsInline
          preload="metadata"
          className="aspect-square"
          src={videoSrc}
          poster="/instruction-01-poster.jpg"
          autoPlay
          muted
        />
      </figure>
      <Text color="indigo" align="center" className="min-h-[72px]" highContrast size="7" weight="bold">
        {title}
      </Text>
    </div>
  );
}

const variantsButton = {
  initial: { opacity: 0, y: -50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50, width: 82 },
};

export function AnimatedState({ children }: { children: React.ReactNode }) {
  const [elementRef, bounds] = useMeasure<HTMLDivElement>();

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        initial="initial"
        animate={{ opacity: 1, y: 0, width: bounds.width }}
        exit="exit"
        variants={variantsButton}
        transition={{ type: "spring", duration: 0.3, bounce: 0 }}
        key={children?.toLocaleString()}
      >
        <span ref={elementRef}>{children}</span>
      </motion.div>
    </AnimatePresence>
  );
}
